import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { CalendarCheck, ChevronDown, HandCoins, Plus, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { EmptyState } from '../../ui/empty-state';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { LoadingState } from '../../ui/loading-state';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Switch } from '../../ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { cn } from '../../../lib/utils';
import { usePermission } from '../../rbac/use-permission';
import { P } from '../../../lib/rbac/permissions.generated';
import {
  billingApi,
  type AccountPosting,
  type ContractAccount,
  type ContractAccountDetail,
  type ContingentTariffOption,
  type ManualPostingKind,
  type PaymentInstrument,
  type StripeMethodOption,
} from '../../../lib/api/billing';
import {
  BILLING_MODEL_LABEL,
  CONTINGENT_MODE_LABEL,
  INTERVALS,
  INTERVAL_LABEL,
  PERIOD_LABEL,
  POSTING_KIND_LABEL,
  formatCents,
  formatIban,
  formatIsoDate,
  ibanProblem,
  newIdempotencyKey,
  parseEuroToCents,
  tariffPriceLabel,
  todayIso,
} from '../../../lib/billing';
import ContingentPanel from './ContingentPanel';
import DocumentsPanel from './DocumentsPanel';
import PersonCollapsibleSection from './PersonCollapsibleSection';
import SepaMandatesPanel from './SepaMandatesPanel';

const FINANZEN = P.buchhaltung.finanzdaten_und_zahlungen;
const NO_INSTRUMENT = 'none';
const NEW_INVOICE = 'new-invoice';
const STRIPE_PREFIX = 'stripe:';
const REVERSIBLE_KINDS = new Set(['charge', 'fee', 'credit', 'write_off']);

export interface ContractRef {
  enrollmentId: number;
  unterrichtsformName: string | null;
  teacherName: string | null;
}

interface Props {
  personId: string | number;
  personName?: string;
  contracts: ContractRef[];
}

function Balance({ cents }: { cents: number }) {
  if (cents > 0) return <span className="font-medium text-destructive tabular-nums">{formatCents(cents)} offen</span>;
  if (cents < 0) return <span className="font-medium text-success tabular-nums">{formatCents(-cents)} Guthaben</span>;
  return <span className="text-muted-foreground">ausgeglichen</span>;
}

function priceSummary(account: ContractAccount): string {
  const model = BILLING_MODEL_LABEL[account.billingModel] ?? account.billingModel;
  if (account.priceCents === null) return model;
  if (account.billingModel === 'periodic') {
    const basis = PERIOD_LABEL[account.pricePeriodMonths ?? account.intervalMonths ?? 1] ?? 'Zeitraum';
    const rhythm = INTERVAL_LABEL[account.intervalMonths ?? 1] ?? '';
    return `${model} · ${formatCents(account.priceCents)} je ${basis}${rhythm ? `, ${rhythm} gezahlt` : ''}`;
  }
  const mode = account.contingentMode ? CONTINGENT_MODE_LABEL[account.contingentMode] : '';
  if (account.tariffId === null) return `${model} · Altpakete`;
  return `${model}${mode ? ` (${mode})` : ''} · ${formatCents(account.priceCents)}`;
}

export default function ContractAccountsSection({ personId, personName = '', contracts }: Props) {
  const canView = usePermission(FINANZEN.offene_posten_ansehen);
  const canEdit = usePermission(FINANZEN.offene_posten_bearbeiten);
  const canSeeInstruments = usePermission(FINANZEN.finanzdaten_ansehen);
  const canEditInstruments = usePermission(FINANZEN.finanzdaten_bearbeiten);
  const canRefund = usePermission(FINANZEN.zahlung_zurueckerstatten);
  const [refundFor, setRefundFor] = useState<ContractAccountDetail | null>(null);

  const [accounts, setAccounts] = useState<ContractAccount[]>([]);
  const [instruments, setInstruments] = useState<PaymentInstrument[]>([]);
  const [stripeMethods, setStripeMethods] = useState<StripeMethodOption[]>([]);
  const [details, setDetails] = useState<Record<number, ContractAccountDetail>>({});
  const [expanded, setExpanded] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [opening, setOpening] = useState<number | null>(null);
  const [postingFor, setPostingFor] = useState<ContractAccountDetail | null>(null);
  const [reversing, setReversing] = useState<AccountPosting | null>(null);
  const [busy, setBusy] = useState(false);
  const [choosingPackage, setChoosingPackage] = useState<{
    enrollmentId: number;
    tariffs: ContingentTariffOption[];
    defaultTariffId: number | null;
  } | null>(null);

  const loadInstruments = useCallback(async () => {
    if (!canSeeInstruments) return;
    const response = await billingApi.listInstruments(personId);
    setInstruments(Array.isArray(response?.items) ? response.items : []);
    setStripeMethods(Array.isArray(response?.availableStripeMethods) ? response.availableStripeMethods : []);
  }, [canSeeInstruments, personId]);

  const load = useCallback(async () => {
    if (!canView || !personId) return;
    setLoading(true);
    try {
      const [response] = await Promise.all([billingApi.listAccounts({ personId }), loadInstruments()]);
      setAccounts(Array.isArray(response?.items) ? response.items : []);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Vertragskonten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [canView, personId, loadInstruments]);

  useEffect(() => {
    void load();
  }, [load]);

  const remember = useCallback((detail: ContractAccountDetail) => {
    setDetails((previous) => ({ ...previous, [detail.id]: detail }));
    setAccounts((previous) => previous.map((account) => (account.id === detail.id ? { ...account, ...detail } : account)));
  }, []);

  const toggle = async (accountId: number) => {
    if (expanded === accountId) {
      setExpanded(null);
      return;
    }
    setExpanded(accountId);
    try {
      remember(await billingApi.getAccount(accountId));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Vertragskonto konnte nicht geladen werden');
    }
  };

  const run = async (action: () => Promise<ContractAccountDetail>, message: string) => {
    setBusy(true);
    try {
      remember(await action());
      toast.success(message);
      return true;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Aktion fehlgeschlagen');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const createAccount = async (enrollmentId: number, tariffId?: number) => {
    const detail = await billingApi.openAccount(enrollmentId, tariffId ? { tariffId } : {});
    toast.success(
      detail.contingent && detail.contingent.packages.length > 0
        ? `Vertragskonto ${detail.accountNumber} angelegt, erstes Paket gebucht`
        : `Vertragskonto ${detail.accountNumber} angelegt`,
    );
    setAccounts((previous) => [detail, ...previous.filter((account) => account.id !== detail.id)]);
    setDetails((previous) => ({ ...previous, [detail.id]: detail }));
    setExpanded(detail.id);
  };

  const openAccount = async (enrollmentId: number) => {
    setOpening(enrollmentId);
    try {
      // Kontingent mit Paket-Tarifen: erst das Paket wählen, es wird sofort gebucht.
      const options = await billingApi.accountOptions(enrollmentId).catch(() => null);
      const tariffs = options?.contingentTariffs ?? [];
      if (options?.model === 'contingent' && tariffs.length > 0) {
        setChoosingPackage({
          enrollmentId,
          tariffs,
          defaultTariffId: options.tariff?.id ?? tariffs[0]?.id ?? null,
        });
        return;
      }
      await createAccount(enrollmentId);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Vertragskonto konnte nicht angelegt werden');
    } finally {
      setOpening(null);
    }
  };

  const chooseInstrument = async (account: ContractAccountDetail, value: string) => {
    await run(async () => {
      let instrumentId: number | null = null;
      if (value === NEW_INVOICE) {
        instrumentId = (await billingApi.createInstrument(personId, { provider: 'invoice' })).id;
      } else if (value.startsWith(STRIPE_PREFIX)) {
        instrumentId = (
          await billingApi.createInstrument(personId, {
            provider: 'stripe',
            paymentMethodId: value.slice(STRIPE_PREFIX.length),
          })
        ).id;
      } else if (value !== NO_INSTRUMENT) {
        instrumentId = Number(value);
      }
      const detail = await billingApi.updateAccount(account.id, { paymentInstrumentId: instrumentId });
      await loadInstruments();
      return detail;
    }, 'Zahlungsmittel gespeichert');
  };

  const chargeFees = async (account: ContractAccountDetail) => {
    setBusy(true);
    try {
      const summary = await billingApi.runPeriodicCharges({ accountId: account.id, through: todayIso() });
      remember(await billingApi.getAccount(account.id));
      toast.success(
        summary.charges > 0
          ? `${summary.charges} Beitr${summary.charges === 1 ? 'ag' : 'äge'} über ${formatCents(summary.totalCents)} gebucht`
          : 'Keine fälligen Beiträge offen',
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Beiträge konnten nicht gebucht werden');
    } finally {
      setBusy(false);
    }
  };

  const contractLabel = useMemo(() => {
    const byEnrollment = new Map(contracts.map((contract) => [contract.enrollmentId, contract]));
    return (enrollmentId: number) => {
      const contract = byEnrollment.get(enrollmentId);
      if (!contract) return `Anmeldung #${enrollmentId}`;
      return [contract.unterrichtsformName || 'Vertrag', contract.teacherName].filter(Boolean).join(' · ');
    };
  }, [contracts]);

  if (!canView) return null;

  const withoutAccount = contracts.filter(
    (contract) => !accounts.some((account) => account.enrollmentId === contract.enrollmentId),
  );
  const activeInstruments = instruments.filter((instrument) => instrument.status === 'active');
  const hasInvoice = activeInstruments.some((instrument) => instrument.provider === 'invoice');
  const unusedStripeMethods = stripeMethods.filter(
    (method) =>
      !instruments.some((instrument) => instrument.provider === 'stripe' && instrument.providerRef === method.paymentMethodId),
  );

  return (
    <PersonCollapsibleSection
      title="Vertragskonten"
      description="Forderungen, Zahlungen und offene Posten je Vertrag"
    >
      {canSeeInstruments && (
        <div className="mb-3">
          <SepaMandatesPanel
            personId={personId}
            personName={personName}
            canEdit={canEditInstruments}
            onChanged={async () => {
              setDetails({});
              setExpanded(null);
              await load();
            }}
          />
        </div>
      )}
      {loading ? (
        <LoadingState message="Vertragskonten werden geladen …" />
      ) : accounts.length === 0 && withoutAccount.length === 0 ? (
        <EmptyState message="Keine Verträge vorhanden." />
      ) : (
        <div className="space-y-3">
          {accounts.map((account) => {
            const detail = details[account.id];
            const isOpen = expanded === account.id;
            const reversed = new Set((detail?.postings ?? []).map((posting) => posting.reversesPostingId).filter(Boolean));
            return (
              <div key={account.id} className="rounded-lg border">
                <button
                  type="button"
                  onClick={() => void toggle(account.id)}
                  aria-expanded={isOpen}
                  className="flex w-full items-center justify-between gap-4 p-4 text-left hover:bg-muted/40"
                >
                  <div className="min-w-0">
                    <div className="font-medium">
                      <span className="tabular-nums">{account.accountNumber}</span> · {contractLabel(account.enrollmentId)}
                    </div>
                    <div className="text-sm text-muted-foreground">{priceSummary(account)}</div>
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    {account.collectionBlocked && (
                      <Badge variant="warning" size="sm">
                        Einzug gesperrt
                      </Badge>
                    )}
                    {account.balanceCents !== undefined && <Balance cents={account.balanceCents} />}
                    <ChevronDown className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')} />
                  </div>
                </button>

                {isOpen && !detail && <LoadingState size="sm" className="py-6" />}
                {isOpen && detail && (
                  <div className="space-y-4 border-t p-4">
                    <div className="grid gap-4 md:grid-cols-3">
                      <div>
                        <div className="text-sm text-muted-foreground">Offene Forderungen</div>
                        <div className="text-lg font-semibold tabular-nums">{formatCents(detail.openDebitCents)}</div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Freies Guthaben</div>
                        <div className="text-lg font-semibold tabular-nums">{formatCents(detail.openCreditCents)}</div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Debitor</div>
                        <div className="text-lg font-semibold tabular-nums">{detail.debtor?.debtorNumber ?? '–'}</div>
                      </div>
                    </div>

                    {detail.contingent && (
                      <ContingentPanel
                        detail={{ ...detail, contingent: detail.contingent }}
                        canEdit={canEdit}
                        busy={busy}
                        onRun={run}
                      />
                    )}

                    {detail.billingModel === 'periodic' && (
                      <PeriodicSettings
                        detail={detail}
                        canEdit={canEdit}
                        busy={busy}
                        onUpdate={(input, message) => run(() => billingApi.updateAccount(detail.id, input), message)}
                        onCharge={() => chargeFees(detail)}
                      />
                    )}

                    <div className="grid gap-4 md:grid-cols-2 md:items-end">
                      <div className="space-y-2">
                        <Label htmlFor={`instrument-${detail.id}`}>Zahlungsmittel</Label>
                        {canEdit && canEditInstruments ? (
                          <Select
                            value={detail.paymentInstrumentId ? String(detail.paymentInstrumentId) : NO_INSTRUMENT}
                            onValueChange={(value) => void chooseInstrument(detail, value)}
                            disabled={busy}
                          >
                            <SelectTrigger id={`instrument-${detail.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={NO_INSTRUMENT}>Keins – wird nicht eingezogen</SelectItem>
                              {activeInstruments.map((instrument) => (
                                <SelectItem key={instrument.id} value={String(instrument.id)}>
                                  {instrument.displayLabel}
                                </SelectItem>
                              ))}
                              {!hasInvoice && <SelectItem value={NEW_INVOICE}>Rechnung / Überweisung hinzufügen</SelectItem>}
                              {unusedStripeMethods.map((method) => (
                                <SelectItem key={method.paymentMethodId} value={`${STRIPE_PREFIX}${method.paymentMethodId}`}>
                                  Stripe: {method.type === 'sepa_debit' ? 'SEPA-Lastschrift' : (method.brand ?? 'Karte')}
                                  {method.last4 ? ` •••• ${method.last4}` : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <div id={`instrument-${detail.id}`} className="text-sm">
                            {detail.paymentInstrument?.displayLabel ?? 'Keins – wird nicht eingezogen'}
                          </div>
                        )}
                      </div>
                      <label className="flex items-center gap-3 text-sm">
                        <Switch
                          checked={detail.collectionBlocked}
                          disabled={!canEdit || busy}
                          onCheckedChange={(checked) =>
                            void run(
                              () => billingApi.updateAccount(detail.id, { collectionBlocked: checked }),
                              checked ? 'Einzug gesperrt' : 'Einzug freigegeben',
                            )
                          }
                        />
                        Einzug sperren, etwa während einer Klärung
                      </label>
                    </div>

                    <DocumentsPanel
                      contractAccountId={detail.id}
                      accountNumber={detail.accountNumber}
                    />

                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium">Buchungen</h4>
                      <div className="flex gap-2">
                        {canRefund && detail.balanceCents < 0 && (
                          <Button variant="outline" size="sm" onClick={() => setRefundFor(detail)} disabled={busy}>
                            <HandCoins className="mr-2 h-4 w-4" />
                            Guthaben erstatten
                          </Button>
                        )}
                        {canEdit && (
                          <Button variant="outline" size="sm" onClick={() => setPostingFor(detail)} disabled={busy}>
                            <Plus className="mr-2 h-4 w-4" />
                            Buchung erfassen
                          </Button>
                        )}
                      </div>
                    </div>
                    {detail.postings.length === 0 ? (
                      <p className="text-sm text-muted-foreground">Noch keine Buchungen.</p>
                    ) : (
                      <div className="overflow-x-auto rounded-md border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Gebucht</TableHead>
                              <TableHead>Fällig</TableHead>
                              <TableHead>Buchung</TableHead>
                              <TableHead className="text-right">Betrag</TableHead>
                              <TableHead className="text-right">Offen</TableHead>
                              {canEdit && <TableHead />}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {detail.postings.map((posting) => (
                              <TableRow key={posting.id} className={cn(reversed.has(posting.id) && 'text-muted-foreground')}>
                                <TableCell className="whitespace-nowrap tabular-nums">{formatIsoDate(posting.postingDate)}</TableCell>
                                <TableCell className="whitespace-nowrap tabular-nums">{formatIsoDate(posting.dueDate)}</TableCell>
                                <TableCell>
                                  <div className="font-medium">{POSTING_KIND_LABEL[posting.kind] ?? posting.kind}</div>
                                  <div className="text-xs text-muted-foreground">{posting.description}</div>
                                </TableCell>
                                <TableCell
                                  className={cn(
                                    'text-right tabular-nums',
                                    posting.amountCents < 0 && 'text-success',
                                  )}
                                >
                                  {posting.amountCents < 0 ? '−' : ''}
                                  {formatCents(Math.abs(posting.amountCents))}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                  {posting.openCents > 0 ? formatCents(posting.openCents) : '–'}
                                </TableCell>
                                {canEdit && (
                                  <TableCell className="text-right">
                                    {REVERSIBLE_KINDS.has(posting.kind) && !reversed.has(posting.id) && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setReversing(posting)}
                                        disabled={busy}
                                        aria-label={`${POSTING_KIND_LABEL[posting.kind]} stornieren`}
                                      >
                                        <Undo2 className="mr-1 h-4 w-4" />
                                        Stornieren
                                      </Button>
                                    )}
                                  </TableCell>
                                )}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {withoutAccount.map((contract) => (
            <div
              key={contract.enrollmentId}
              className="flex items-center justify-between gap-4 rounded-lg border border-dashed p-4"
            >
              <div>
                <div className="font-medium">{contractLabel(contract.enrollmentId)}</div>
                <div className="text-sm text-muted-foreground">Noch kein Vertragskonto</div>
              </div>
              {canEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void openAccount(contract.enrollmentId)}
                  disabled={opening !== null}
                >
                  {opening === contract.enrollmentId ? 'Legt an …' : 'Vertragskonto anlegen'}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {postingFor && (
        <PostingDialog
          account={postingFor}
          onClose={() => setPostingFor(null)}
          onSubmit={async (input) => {
            if (await run(() => billingApi.postToAccount(postingFor.id, input), 'Buchung erfasst')) {
              setPostingFor(null);
            }
          }}
          busy={busy}
        />
      )}

      {choosingPackage && (
        <OpenContingentDialog
          label={contractLabel(choosingPackage.enrollmentId)}
          tariffs={choosingPackage.tariffs}
          defaultTariffId={choosingPackage.defaultTariffId}
          onClose={() => setChoosingPackage(null)}
          onSubmit={async (tariffId) => {
            try {
              await createAccount(choosingPackage.enrollmentId, tariffId);
              setChoosingPackage(null);
            } catch (error) {
              toast.error(error instanceof Error ? error.message : 'Vertragskonto konnte nicht angelegt werden');
            }
          }}
        />
      )}

      {refundFor && (
        <RefundDialog
          account={refundFor}
          defaultName={personName}
          onClose={() => setRefundFor(null)}
          onCreated={async () => {
            const accountId = refundFor.id;
            setRefundFor(null);
            remember(await billingApi.getAccount(accountId));
          }}
        />
      )}

      {reversing && (
        <Dialog open onOpenChange={(open) => !open && setReversing(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Buchung stornieren?</DialogTitle>
              <DialogDescription>
                {POSTING_KIND_LABEL[reversing.kind]} über {formatCents(Math.abs(reversing.amountCents))} („
                {reversing.description}“). Es entsteht eine Gegenbuchung; die ursprüngliche Buchung bleibt sichtbar.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReversing(null)} disabled={busy}>
                Zurück
              </Button>
              <Button
                variant="destructive"
                disabled={busy}
                onClick={async () => {
                  if (await run(() => billingApi.reversePosting(reversing.id), 'Buchung storniert')) {
                    setReversing(null);
                  }
                }}
              >
                Stornieren
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </PersonCollapsibleSection>
  );
}

interface PeriodicSettingsProps {
  detail: ContractAccountDetail;
  canEdit: boolean;
  busy: boolean;
  onUpdate: (input: { intervalMonths?: number; serviceEnd?: string | null }, message: string) => Promise<boolean>;
  onCharge: () => Promise<void>;
}

function PeriodicSettings({ detail, canEdit, busy, onUpdate, onCharge }: PeriodicSettingsProps) {
  const [endDate, setEndDate] = useState(detail.serviceEnd ?? '');
  useEffect(() => setEndDate(detail.serviceEnd ?? ''), [detail.serviceEnd]);
  const feesBooked = detail.postings.some((posting) => posting.sourceType === 'periodic_fee');

  return (
    <div className="grid gap-4 rounded-md border bg-muted/20 p-3 md:grid-cols-3">
      <div className="space-y-2">
        <Label htmlFor={`interval-${detail.id}`}>Zahlweise</Label>
        <Select
          value={String(detail.intervalMonths ?? 1)}
          onValueChange={(value) => void onUpdate({ intervalMonths: Number(value) }, 'Zahlweise geändert')}
          disabled={!canEdit || busy || feesBooked}
        >
          <SelectTrigger id={`interval-${detail.id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {INTERVALS.map((value) => (
              <SelectItem key={value} value={String(value)}>
                {INTERVAL_LABEL[value]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {feesBooked && (
          <p className="text-xs text-muted-foreground">Nach dem ersten Beitrag nicht mehr änderbar.</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor={`service-end-${detail.id}`}>
          Vertragsende <span className="font-normal text-muted-foreground">(Beginn {formatIsoDate(detail.serviceStart)})</span>
        </Label>
        <div className="flex gap-2">
          <Input
            id={`service-end-${detail.id}`}
            type="date"
            value={endDate}
            min={detail.serviceStart ?? undefined}
            onChange={(event) => setEndDate(event.target.value)}
            disabled={!canEdit || busy}
          />
          {canEdit && endDate !== (detail.serviceEnd ?? '') && (
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() =>
                void onUpdate(
                  { serviceEnd: endDate || null },
                  endDate ? 'Vertragsende gesetzt' : 'Vertragsende entfernt',
                )
              }
            >
              Übernehmen
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground">Kündigungen setzen das Ende automatisch.</p>
      </div>
      <div className="space-y-2">
        <div className="text-sm font-medium">Beiträge gebucht bis</div>
        <div className="text-sm tabular-nums">{formatIsoDate(detail.billedThrough)}</div>
        {canEdit && (
          <Button variant="outline" size="sm" onClick={() => void onCharge()} disabled={busy}>
            <CalendarCheck className="mr-2 h-4 w-4" />
            Fällige Beiträge buchen
          </Button>
        )}
      </div>
    </div>
  );
}

const MANUAL_KINDS: Array<{ value: ManualPostingKind; label: string; hint: string }> = [
  { value: 'charge', label: 'Forderung', hint: 'Der Kunde schuldet diesen Betrag.' },
  { value: 'fee', label: 'Gebühr', hint: 'Zusätzliche Gebühr, etwa für eine Mahnung.' },
  { value: 'credit', label: 'Gutschrift', hint: 'Wird mit offenen und künftigen Forderungen verrechnet.' },
  { value: 'write_off', label: 'Ausbuchung', hint: 'Forderung wird nicht mehr eingezogen.' },
];

interface PostingDialogProps {
  account: ContractAccountDetail;
  busy: boolean;
  onClose: () => void;
  onSubmit: (input: {
    kind: ManualPostingKind;
    amountCents: number;
    description: string;
    dueDate?: string;
    idempotencyKey: string;
  }) => Promise<void>;
}

function PostingDialog({ account, busy, onClose, onSubmit }: PostingDialogProps) {
  const [kind, setKind] = useState<ManualPostingKind>('charge');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState(todayIso());
  // Ein Schlüssel je Dialog: ein Doppelklick bucht nicht zweimal.
  const [idempotencyKey] = useState(newIdempotencyKey);
  const hint = MANUAL_KINDS.find((entry) => entry.value === kind)?.hint;
  // Kopiergebühr des Standorts als Vorbelegung (T-0105).
  const [kopiergebuehr, setKopiergebuehr] = useState<number | null>(null);
  useEffect(() => {
    if (!account.standortId) return;
    let cancelled = false;
    api
      .get<{ kopiergebuehr?: { enabled?: boolean; amountBrutto?: number } }>(
        `/api/settings/accounting/fees/${account.standortId}`,
      )
      .then((fees) => {
        const betrag = Number(fees?.kopiergebuehr?.amountBrutto || 0);
        if (!cancelled && fees?.kopiergebuehr?.enabled && betrag > 0) setKopiergebuehr(betrag);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [account.standortId]);

  const submit = () => {
    const amountCents = parseEuroToCents(amount);
    if (amountCents === null || amountCents <= 0) {
      toast.error('Betrag größer als 0 angeben, z. B. 24,50');
      return;
    }
    void onSubmit({
      kind,
      amountCents,
      description: description.trim(),
      dueDate: kind === 'charge' || kind === 'fee' ? dueDate : undefined,
      idempotencyKey,
    });
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Buchung erfassen</DialogTitle>
          <DialogDescription>Vertragskonto {account.accountNumber}. Buchungen lassen sich nur stornieren, nicht ändern.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {kopiergebuehr !== null && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setKind('fee');
                setAmount(kopiergebuehr.toFixed(2).replace('.', ','));
                setDescription('Kopiergebühr');
              }}
            >
              Kopiergebühr übernehmen ({kopiergebuehr.toFixed(2).replace('.', ',')} €)
            </Button>
          )}
          <div className="space-y-2">
            <Label htmlFor="posting-kind">Art</Label>
            <Select value={kind} onValueChange={(value) => setKind(value as ManualPostingKind)}>
              <SelectTrigger id="posting-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MANUAL_KINDS.map((entry) => (
                  <SelectItem key={entry.value} value={entry.value}>
                    {entry.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="posting-amount">Betrag (€)</Label>
              <Input
                id="posting-amount"
                inputMode="decimal"
                placeholder="0,00"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
            </div>
            {(kind === 'charge' || kind === 'fee') && (
              <div className="space-y-2">
                <Label htmlFor="posting-due">Fällig am</Label>
                <Input id="posting-due" type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} />
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="posting-description">Buchungstext</Label>
            <Input
              id="posting-description"
              maxLength={140}
              placeholder="z. B. Beitrag Oktober 2026"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button onClick={submit} disabled={busy || !description.trim() || !amount.trim()}>
            Buchen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface RefundDialogProps {
  account: ContractAccountDetail;
  defaultName: string;
  onClose: () => void;
  onCreated: () => Promise<void>;
}

function RefundDialog({ account, defaultName, onClose, onCreated }: RefundDialogProps) {
  const available = Math.max(-account.balanceCents, 0);
  const usesMandate = account.paymentInstrument?.provider === 'hausbank_sepa';
  const [amount, setAmount] = useState((available / 100).toFixed(2).replace('.', ','));
  const [toMandate, setToMandate] = useState(usesMandate);
  const [name, setName] = useState(defaultName);
  const [iban, setIban] = useState('');
  const [remittance, setRemittance] = useState(`Erstattung Guthaben ${account.accountNumber}`);
  const [idempotencyKey] = useState(newIdempotencyKey);
  const [saving, setSaving] = useState(false);
  const ibanError = !toMandate && iban.trim() ? ibanProblem(iban) : null;

  const save = async () => {
    const amountCents = parseEuroToCents(amount);
    if (amountCents === null || amountCents <= 0 || amountCents > available) {
      toast.error(`Betrag zwischen 0,01 € und ${formatCents(available)} angeben`);
      return;
    }
    setSaving(true);
    try {
      await billingApi.createRefund(account.id, {
        amountCents,
        recipientName: toMandate ? undefined : name.trim(),
        iban: toMandate ? undefined : iban.trim(),
        remittance: remittance.trim() || undefined,
        idempotencyKey,
      });
      toast.success(`Erstattung über ${formatCents(amountCents)} vorgemerkt – sie geht mit dem nächsten Überweisungslauf raus`);
      await onCreated();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erstattung fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Guthaben erstatten</DialogTitle>
          <DialogDescription>
            Vertragskonto {account.accountNumber} · Guthaben {formatCents(available)}. Die Erstattung wird sofort
            gegen das Guthaben gebucht und unter Zahlungsläufe → Erstattungen überwiesen.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="refund-amount">Betrag (€)</Label>
            <Input id="refund-amount" inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} />
          </div>
          {usesMandate && (
            <label className="flex items-center gap-3 text-sm">
              <Switch checked={toMandate} onCheckedChange={setToMandate} />
              An das Konto aus dem SEPA-Mandat ({account.paymentInstrument?.displayLabel})
            </label>
          )}
          {!toMandate && (
            <>
              <div className="space-y-2">
                <Label htmlFor="refund-name">Empfänger</Label>
                <Input id="refund-name" maxLength={70} value={name} onChange={(event) => setName(event.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="refund-iban">IBAN</Label>
                <Input
                  id="refund-iban"
                  autoComplete="off"
                  value={iban}
                  onChange={(event) => setIban(event.target.value)}
                  onBlur={() => iban.trim() && !ibanError && setIban(formatIban(iban))}
                  aria-invalid={Boolean(ibanError)}
                />
                {ibanError && <p className="text-sm text-destructive">{ibanError}</p>}
              </div>
            </>
          )}
          <div className="space-y-2">
            <Label htmlFor="refund-remittance">Verwendungszweck</Label>
            <Input
              id="refund-remittance"
              maxLength={140}
              value={remittance}
              onChange={(event) => setRemittance(event.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button
            onClick={() => void save()}
            disabled={saving || (!toMandate && (!name.trim() || !iban.trim() || Boolean(ibanError)))}
          >
            {saving ? 'Bucht …' : 'Erstattung vormerken'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface OpenContingentDialogProps {
  label: string;
  tariffs: ContingentTariffOption[];
  defaultTariffId: number | null;
  onClose: () => void;
  onSubmit: (tariffId: number) => Promise<void>;
}

function OpenContingentDialog({ label, tariffs, defaultTariffId, onClose, onSubmit }: OpenContingentDialogProps) {
  const [tariffId, setTariffId] = useState(String(defaultTariffId ?? tariffs[0]?.id ?? ''));
  const [saving, setSaving] = useState(false);
  const tariff = tariffs.find((entry) => String(entry.id) === tariffId);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Vertragskonto mit Kontingent anlegen</DialogTitle>
          <DialogDescription>
            {label}. Das gewählte Paket wird zum Vertragsbeginn gebucht und ist zugleich das Folgepaket, wenn die
            Einheiten aufgebraucht sind.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="open-contingent-tariff">Paket</Label>
          <Select value={tariffId} onValueChange={setTariffId}>
            <SelectTrigger id="open-contingent-tariff">
              <SelectValue placeholder="Paket wählen" />
            </SelectTrigger>
            <SelectContent>
              {tariffs.map((entry) => (
                <SelectItem key={entry.id} value={String(entry.id)}>
                  {entry.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {tariff && (
            <p className="text-sm text-muted-foreground">
              {CONTINGENT_MODE_LABEL[tariff.contingentMode ?? 'prepaid']} · {tariffPriceLabel(tariff)}
              {tariff.contingentMode === 'installments' && tariff.installmentCount
                ? ` (erste Rate zum Vertragsbeginn)`
                : ''}
              {tariff.contingentMode === 'usage_based' ? ' – ohne Paket, monatliche Abrechnung.' : ''}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button
            disabled={saving || !tariff}
            onClick={async () => {
              setSaving(true);
              try {
                await onSubmit(Number(tariffId));
              } finally {
                setSaving(false);
              }
            }}
          >
            {saving ? 'Legt an …' : 'Anlegen und buchen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
